import React, { useState, useRef, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchManufacturers, createManufacturerThunk } from "../../../redux/Thunk/Product/ProductThunks";

const ManufacturerSelector = ({
  value,
  onChange,
  onBlur,
  isError = false,
  errorMessage = "",
  disabled = false,
  label = "Manufacturer*",
  labelName,
  labelClassName = "block text-black-lose text-[13px] font-medium mb-2",
  placeholder = "Select or search manufacturer...",
}) => {
  const displayLabel = labelName || label;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const { manufacturersList } = useSelector((state) => state.product);

  // Fetch manufacturers on mount if not yet loaded
  useEffect(() => {
    if (!manufacturersList || manufacturersList.length === 0) {
      dispatch(fetchManufacturers());
    }
  }, [dispatch, manufacturersList]);

  // Click outside listener
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearchQuery("");
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onBlur]);

  // Auto focus search input when opened
  useEffect(() => {
    if (isOpen) {
      setHighlightedIndex(0);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Filtered and sorted manufacturers
  const filteredManufacturers = useMemo(() => {
    const list = Array.isArray(manufacturersList) ? manufacturersList : [];
    if (!searchQuery.trim()) {
      return list;
    }
    const q = searchQuery.trim().toLowerCase();
    return list.filter((m) => (m.name || "").toLowerCase().includes(q));
  }, [manufacturersList, searchQuery]);

  // Check if search query exactly matches an existing manufacturer (case-insensitive)
  const exactMatchExists = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return true;
    return (manufacturersList || []).some(
      (m) => (m.name || "").trim().toLowerCase() === q
    );
  }, [manufacturersList, searchQuery]);

  const handleSelect = (name) => {
    if (disabled) return;
    if (onChange) {
      onChange(name);
    }
    setIsOpen(false);
    setSearchQuery("");
    if (onBlur) onBlur();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (disabled) return;
    if (onChange) {
      onChange("");
    }
    if (onBlur) onBlur();
  };

  const handleCreateNew = async () => {
    const nameToAdd = searchQuery.trim();
    if (!nameToAdd || isCreating) return;

    setIsCreating(true);
    try {
      const result = await dispatch(createManufacturerThunk({ name: nameToAdd })).unwrap();
      const createdName = result?.name || nameToAdd;
      handleSelect(createdName);
    } catch (err) {
      console.error("Failed to create manufacturer:", err);
    } finally {
      setIsCreating(false);
    }
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const totalItems = filteredManufacturers.length + (!exactMatchExists && searchQuery.trim() ? 1 : 0);

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1 < totalItems ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : totalItems - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex < filteredManufacturers.length) {
        const item = filteredManufacturers[highlightedIndex];
        if (item) handleSelect(item.name);
      } else if (!exactMatchExists && searchQuery.trim()) {
        handleCreateNew();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setSearchQuery("");
      if (onBlur) onBlur();
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    if (listRef.current) {
      const activeEl = listRef.current.querySelector(`[data-index="${highlightedIndex}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedIndex]);

  return (
    <div
      ref={containerRef}
      className="w-full relative"
      onKeyDown={handleKeyDown}
    >
      {displayLabel && (
        <label className={labelClassName}>
          {displayLabel}
        </label>
      )}

      {/* Trigger Button - Perfectly matches ProductTypeSelector styling */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full min-h-[42px] px-3.5 py-2 rounded-[8px] border flex items-center justify-between transition-all text-left shadow-2xs ${
          isError ? "border-orange-500 bg-white" : "border-primary-200 bg-white"
        } ${
          disabled
            ? "bg-primary-50 text-gray-500 cursor-not-allowed border-primary-100"
            : "hover:border-primary-400 cursor-pointer"
        }`}
      >
        <span
          className={`text-[13px] font-medium truncate ${
            value ? "text-primary-700 font-semibold" : "text-gray-400 italic font-normal"
          }`}
          title={value || ""}
        >
          {value || placeholder}
        </span>

        <div className="flex items-center gap-1.5 shrink-0 ml-2">
          {value && !disabled && (
            <span
              onClick={handleClear}
              title="Clear manufacturer"
              className="w-4 h-4 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors cursor-pointer"
            >
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </span>
          )}
          <svg
            className={`w-4 h-4 text-primary-400 shrink-0 transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Floating Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-primary-200 rounded-[8px] shadow-xl z-50 overflow-hidden divide-y divide-gray-100 animate-in fade-in zoom-in-95 duration-100">
          {/* Search Header */}
          <div className="p-2 bg-gray-50/90">
            <div className="relative flex items-center">
              <svg
                className="w-4 h-4 text-gray-400 absolute left-2.5 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setHighlightedIndex(0);
                }}
                placeholder="Search manufacturer or type new..."
                className="w-full pl-8 pr-7 py-1.5 text-xs bg-white border border-gray-200 rounded-md outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all text-gray-800 placeholder:text-gray-400 font-medium"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-2 text-gray-400 hover:text-gray-600 cursor-pointer"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <div className="mt-1 px-1 flex items-center justify-between text-[10px] text-gray-400 font-semibold tracking-wider uppercase">
              <span>{filteredManufacturers.length} found</span>
              <span>A - Z</span>
            </div>
          </div>

          {/* List of Options */}
          <div ref={listRef} className="max-h-56 overflow-y-auto p-1 divide-y divide-gray-50">
            {filteredManufacturers.length > 0 ? (
              filteredManufacturers.map((mfg, idx) => {
                const isSelected = String(value || "").trim().toLowerCase() === String(mfg.name || "").trim().toLowerCase();
                const isHighlighted = idx === highlightedIndex;

                return (
                  <button
                    key={mfg.id || mfg.name}
                    data-index={idx}
                    type="button"
                    onClick={() => handleSelect(mfg.name)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`w-full text-left px-3 py-2 text-[13px] rounded-[6px] flex items-center justify-between transition-colors cursor-pointer my-0.5 ${
                      isSelected
                        ? "bg-primary-50 text-primary-700 font-semibold"
                        : isHighlighted
                        ? "bg-primary-50/60 text-primary-800 font-medium"
                        : "text-gray-700 hover:bg-primary-50/50 font-medium"
                    }`}
                  >
                    <span className="truncate pr-2">{mfg.name}</span>
                    {isSelected && (
                      <svg
                        className="w-4 h-4 text-primary-600 shrink-0"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2.5}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="py-3 px-2 text-center text-xs text-gray-400 italic">
                No matching manufacturer found
              </div>
            )}

            {/* Quick Add Option */}
            {!exactMatchExists && searchQuery.trim() && (
              <div className="pt-1 mt-1 border-t border-gray-100">
                <button
                  type="button"
                  data-index={filteredManufacturers.length}
                  onClick={handleCreateNew}
                  disabled={isCreating}
                  onMouseEnter={() => setHighlightedIndex(filteredManufacturers.length)}
                  className={`w-full text-left px-3 py-2 text-xs rounded-[6px] flex items-center gap-2 transition-colors cursor-pointer ${
                    highlightedIndex === filteredManufacturers.length
                      ? "bg-emerald-50 text-emerald-800 font-bold"
                      : "text-emerald-700 hover:bg-emerald-50/70 font-semibold"
                  }`}
                >
                  <svg className="w-3.5 h-3.5 shrink-0 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  <span className="truncate">
                    {isCreating ? "Adding..." : `Add "${searchQuery.trim()}" as new manufacturer`}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {isError && errorMessage && (
        <div className="text-orange-800 text-[11px] uppercase mt-1 font-medium">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ManufacturerSelector;
